/**
 * OpuBit-Flags Permission System
 *
 * Jede Berechtigung ist ein einzelnes Bit in einer BIGINT-Zahl.
 * Operationen:
 *   Hinzufügen:  permissions |= PERMISSION
 *   Entfernen:   permissions &= ~PERMISSION
 *   Prüfen:      (permissions & PERMISSION) === PERMISSION
 *   Kombinieren: PERM1 | PERM2 | PERM3
 */

// ─── Permission Flags ────────────────────────────────────────────────
export const OpuPermissions = {
  NONE: 0,

  // ── Base (0–4) ──────────────────────────────────────────────────────
  /** Kann eigenes Dashboard sehen */
  VIEW_DASHBOARD: 1 << 0, // 1
  /** Kann eigenes Profil verwalten */
  MANAGE_PROFILE: 1 << 1, // 2
  /** Kann Auktionen ansehen */
  VIEW_AUCTIONS: 1 << 2, // 4
  /** Kann auf Auktionen bieten */
  BID_ON_AUCTIONS: 1 << 3, // 8
  /** Kann die Watchlist verwenden */
  USE_WATCHLIST: 1 << 4, // 16

  // ── Feature Access (5–8) ────────────────────────────────────────────
  /** Zugang zum Marktplatz (ersetzt alpha_features 'market') */
  ACCESS_MARKET: 1 << 5, // 32
  /** Zugang zum Shards-Rechner */
  ACCESS_SHARDS: 1 << 6, // 64
  /** Kann Developer-API Tools nutzen */
  USE_DEVELOPER_API: 1 << 7, // 128
  /** Zugang zu Beta-Features */
  ACCESS_BETA_FEATURES: 1 << 8, // 256
  /** Zugang zu Alpha-Features (restriktiver als Beta) */
  ACCESS_ALPHA_FEATURES: 1 << 18, // 262144

  // ── Moderation (9–11) ───────────────────────────────────────────────
  /** Kann alle Nutzerprofile einsehen */
  VIEW_ALL_USERS: 1 << 9, // 512
  /** Kann Auktionen moderieren */
  MODERATE_AUCTIONS: 1 << 10, // 1024
  /** Kann Nutzer verwalten */
  MANAGE_USERS: 1 << 11, // 2048

  // ── Admin (12–16) ───────────────────────────────────────────────────
  /** Kann das Admin-Panel sehen */
  VIEW_ADMIN_PANEL: 1 << 12, // 4096
  /** Kann Admin-Statistiken einsehen */
  VIEW_ADMIN_STATS: 1 << 13, // 8192
  /** Kann API-Anträge verwalten */
  MANAGE_API_REQUESTS: 1 << 14, // 16384
  /** Kann Admins hinzufügen/entfernen */
  MANAGE_ADMINS: 1 << 15, // 32768
  /** Kann Berechtigungen verwalten */
  MANAGE_PERMISSIONS: 1 << 16, // 65536

  // ── Account-Verwaltung (19) ───────────────────────────────────────
  /** Kann Minecraft-Verifizierungen zurücksetzen */
  RESET_VERIFICATION: 1 << 19, // 524288

  // ── Super (17) ──────────────────────────────────────────────────────
  /** Voller Zugriff auf alle Funktionen */
  ADMINISTRATOR: 1 << 17, // 131072
} as const;

export type OpuPermissionKey = keyof typeof OpuPermissions;
export type OpuPermissionValue = (typeof OpuPermissions)[OpuPermissionKey];

// ─── Predefined Roles ────────────────────────────────────────────────

const _USER =
  OpuPermissions.VIEW_DASHBOARD |
  OpuPermissions.MANAGE_PROFILE |
  OpuPermissions.VIEW_AUCTIONS |
  OpuPermissions.BID_ON_AUCTIONS |
  OpuPermissions.USE_WATCHLIST |
  OpuPermissions.ACCESS_SHARDS;

const _BETA_TESTER =
  _USER |
  OpuPermissions.ACCESS_BETA_FEATURES;

const _MODERATOR =
  _BETA_TESTER |
  OpuPermissions.VIEW_ALL_USERS |
  OpuPermissions.MODERATE_AUCTIONS;

const _ADMIN =
  _MODERATOR |
  OpuPermissions.ACCESS_ALPHA_FEATURES |
  OpuPermissions.ACCESS_MARKET |
  OpuPermissions.USE_DEVELOPER_API |
  OpuPermissions.VIEW_ADMIN_PANEL |
  OpuPermissions.VIEW_ADMIN_STATS |
  OpuPermissions.MANAGE_API_REQUESTS |
  OpuPermissions.MANAGE_ADMINS |
  OpuPermissions.MANAGE_PERMISSIONS |
  OpuPermissions.MANAGE_USERS |
  OpuPermissions.RESET_VERIFICATION;

export const OpuRoles = {
  /** Gast – keine Berechtigungen */
  GUEST: OpuPermissions.NONE,
  /** Standard-Nutzer */
  USER: _USER,
  /** Beta-Tester mit erweiterten Features */
  BETA_TESTER: _BETA_TESTER,
  /** Moderator */
  MODERATOR: _MODERATOR,
  /** Admin – alle einzelnen Flags */
  ADMIN: _ADMIN,
  /** Super-Admin – ADMINISTRATOR-Bit */
  SUPER_ADMIN: OpuPermissions.ADMINISTRATOR,
} as const;

// ─── Permission Manager Utility ──────────────────────────────────────

export class PermissionManager {
  private _permissions: number;

  constructor(permissions: number = 0) {
    this._permissions = permissions;
  }

  /** Aktuelle Berechtigungszahl */
  get value(): number {
    return this._permissions;
  }

  /** Berechtigung hinzufügen (OR) */
  add(permission: number): this {
    this._permissions |= permission;
    return this;
  }

  /** Berechtigung entfernen (AND NOT) */
  remove(permission: number): this {
    this._permissions &= ~permission;
    return this;
  }

  /** Prüft ob eine bestimmte Berechtigung vorhanden ist */
  has(permission: number): boolean {
    // ADMINISTRATOR hat immer alle Rechte
    if ((this._permissions & OpuPermissions.ADMINISTRATOR) === OpuPermissions.ADMINISTRATOR) {
      return true;
    }
    return (this._permissions & permission) === permission;
  }

  /** Prüft ob mindestens eine der Berechtigungen vorhanden ist */
  hasAny(...permissions: number[]): boolean {
    if ((this._permissions & OpuPermissions.ADMINISTRATOR) === OpuPermissions.ADMINISTRATOR) {
      return true;
    }
    const combined = permissions.reduce((a, b) => a | b, 0);
    return (this._permissions & combined) !== 0;
  }

  /** Prüft ob alle Berechtigungen vorhanden sind */
  hasAll(...permissions: number[]): boolean {
    if ((this._permissions & OpuPermissions.ADMINISTRATOR) === OpuPermissions.ADMINISTRATOR) {
      return true;
    }
    const combined = permissions.reduce((a, b) => a | b, 0);
    return (this._permissions & combined) === combined;
  }

  /** Gibt eine Liste aller aktiven Berechtigungsnamen zurück */
  toList(): OpuPermissionKey[] {
    const active: OpuPermissionKey[] = [];
    for (const [name, value] of Object.entries(OpuPermissions)) {
      if (value !== 0 && this.has(value)) {
        active.push(name as OpuPermissionKey);
      }
    }
    return active;
  }

  /** Setzt die Berechtigungen auf eine Rolle */
  setRole(role: number): this {
    this._permissions = role;
    return this;
  }

  /** Erstellt eine Kopie */
  clone(): PermissionManager {
    return new PermissionManager(this._permissions);
  }
}

// ─── Permission Labels (für UI) ──────────────────────────────────────

export const PERMISSION_LABELS: Record<OpuPermissionKey, string> = {
  NONE: "Keine",
  VIEW_DASHBOARD: "Dashboard ansehen",
  MANAGE_PROFILE: "Profil verwalten",
  VIEW_AUCTIONS: "Auktionen ansehen",
  BID_ON_AUCTIONS: "Auf Auktionen bieten",
  USE_WATCHLIST: "Watchlist verwenden",
  ACCESS_MARKET: "Marktplatz-Zugang",
  ACCESS_SHARDS: "Shards-Rechner",
  USE_DEVELOPER_API: "Developer API",
  ACCESS_BETA_FEATURES: "Beta-Features",
  ACCESS_ALPHA_FEATURES: "Alpha-Features",
  VIEW_ALL_USERS: "Alle Nutzer einsehen",
  MODERATE_AUCTIONS: "Auktionen moderieren",
  MANAGE_USERS: "Nutzer verwalten",
  VIEW_ADMIN_PANEL: "Admin-Panel",
  VIEW_ADMIN_STATS: "Admin-Statistiken",
  MANAGE_API_REQUESTS: "API-Anträge verwalten",
  MANAGE_ADMINS: "Admins verwalten",
  MANAGE_PERMISSIONS: "Berechtigungen verwalten",
  RESET_VERIFICATION: "Verifizierung zurücksetzen",
  ADMINISTRATOR: "Administrator",
};

export const ROLE_LABELS: Record<keyof typeof OpuRoles, string> = {
  GUEST: "Gast",
  USER: "Nutzer",
  BETA_TESTER: "Beta-Tester",
  MODERATOR: "Moderator",
  ADMIN: "Admin",
  SUPER_ADMIN: "Super-Admin",
};

// ─── Helper: Determine the "best" role label for a permission value ──

export function getRoleForPermissions(permissions: number): keyof typeof OpuRoles {
  if ((permissions & OpuPermissions.ADMINISTRATOR) === OpuPermissions.ADMINISTRATOR) return "SUPER_ADMIN";
  if ((permissions & OpuRoles.ADMIN) === OpuRoles.ADMIN) return "ADMIN";
  if ((permissions & OpuRoles.MODERATOR) === OpuRoles.MODERATOR) return "MODERATOR";
  if ((permissions & OpuRoles.BETA_TESTER) === OpuRoles.BETA_TESTER) return "BETA_TESTER";
  if ((permissions & OpuRoles.USER) === OpuRoles.USER) return "USER";
  return "GUEST";
}
